import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { AnimatePresence, motion } from 'framer-motion'
import { RiComputerLine, RiLoader4Line } from 'react-icons/ri'

const BACKGROUND_IMAGES = []

export default function HeroPreviewPage() {
  const [images, setImages] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlashing, setIsFlashing] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/public/image-types/hero_background/images')
      .then(({ data }) => {
        setImages(data.images || [])
      })
      .catch(() => setImages([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (images.length === 0) return
    const interval = setInterval(() => {
      setIsFlashing(true)
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length)
      }, 150)
      setTimeout(() => {
        setIsFlashing(false)
      }, 300)
    }, 6000)
    return () => clearInterval(interval)
  }, [images.length])

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <RiLoader4Line className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Memuat...</span>
      </div>
    )
  }

  const bgUrls = images.map((i) => i.url)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Tampilan Halaman Utama</h2>
          <p className="text-sm text-muted-foreground">
            Ini adalah bentuk tampilan halaman utama website Asa Hikari Mulya. Gambar yang aktif akan muncul secara bergilir setiap 6 detik.
          </p>
        </div>
      </div>

      {bgUrls.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/40 p-16 text-center">
          <RiComputerLine className="mb-3 text-5xl text-muted-foreground/40" />
          <p className="text-sm font-medium text-muted-foreground">Belum ada gambar hero yang aktif</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Upload dan aktifkan gambar di halaman <a href="/images" className="text-primary underline">Gambar</a>.
          </p>
        </div>
      )}

      {bgUrls.length > 0 && (
        <div className="relative aspect-[16/9] overflow-hidden rounded-lg border bg-gray-900">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="absolute inset-0"
              initial={{ opacity: 0, scale: 1 }}
              animate={{
                opacity: 1,
                scale: isFlashing ? 1.05 : 1.1,
                transition: {
                  opacity: { duration: 0.5 },
                  scale: {
                    duration: isFlashing ? 0.3 : 5.7,
                    ease: isFlashing ? 'easeOut' : 'linear',
                  },
                },
              }}
              exit={{ opacity: 0, scale: 1.15 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className="h-full w-full bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${bgUrls[currentIndex]}')`,
                }}
              />
            </motion.div>
          </AnimatePresence>

          <AnimatePresence>
            {isFlashing && (
              <motion.div
                className="absolute inset-0 z-20 bg-gray-100"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.8, 0], transition: { duration: 0.3, times: [0, 0.5, 1], ease: 'easeInOut' } }}
                exit={{ opacity: 0 }}
              />
            )}
          </AnimatePresence>

          {/* Content overlay — matches frontend HeroSection */}
          <div className="relative z-10 flex h-full flex-col items-center justify-center text-center text-white px-4 sm:px-6">
            <div className="mx-auto space-y-3 sm:space-y-4 md:space-y-6 max-w-3xl">
              {/* Title */}
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                <div className="flex-grow border-t border-gray-100 opacity-10 max-w-8 sm:max-w-none" />
                <div className="flex flex-col text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                  <span>Asa</span>
                  <span>Hikari</span>
                  <span>Mulya</span>
                </div>
                <div className="flex-grow border-t border-gray-100 opacity-10 max-w-8 sm:max-w-none" />
              </div>

              {/* Quote */}
              <p className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold italic px-2 leading-tight">
                "Kami siap memberikan bimbingan dan segala hal yang perlu Anda ketahui untuk mempersiapkan diri bekerja di Jepang"
              </p>

              {/* Description */}
              <p className="text-xs sm:text-sm md:text-base lg:text-lg mt-2 max-w-xs sm:max-w-xl md:max-w-2xl mx-auto leading-relaxed px-2">
                Asa Hikari Mulya adalah pusat pelatihan bahasa Jepang terpercaya yang mempersiapkan Anda untuk bekerja di Jepang melalui program Specified Skilled Worker (SSW) dan Magang. Dapatkan pelatihan intensif, sertifikasi resmi, dan penempatan kerja dengan kurikulum berbasis kebutuhan pasar kerja Jepang.
              </p>

              {/* CTA Button */}
              <button className="mt-2 rounded-bl-2xl rounded-tr-2xl bg-blue-800 px-6 py-2.5 text-sm sm:text-base font-semibold hover:bg-blue-700 transition-all inline-flex items-center gap-2">
                Join Now
              </button>
            </div>
          </div>

          {/* Image counter */}
          <div className="absolute right-3 top-3 z-30 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
            {currentIndex + 1} / {bgUrls.length}
          </div>
        </div>
      )}

      {/* Dots */}
      {bgUrls.length > 1 && (
        <div className="flex justify-center gap-2">
          {bgUrls.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              title={`Lihat gambar ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Side banner preview */}
      <div className="flex items-center gap-3 rounded-lg border bg-background p-4">
        <RiComputerLine className="text-xl text-primary flex-shrink-0" />
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Catatan:</span> Ini adalah simulasi cara tampilan halaman depan website.
          Semua gambar yang aktif pada slot <strong>Hero Background</strong> akan ditampilkan bergilir — sama persis seperti yang dilihat pengunjung website.
        </div>
      </div>
    </div>
  )
}
